/**
 * @author Fernando Ruano
 * @file saga.js
 * @description Auth Saga
 */

// Dependencies
import { takeLatest, put, call } from 'redux-saga/effects';

// Services
import authenticationService from '@nephro/services/authentication-service';

// Global actions
import {
  setResponse,
  setLoading,
  setUser
} from '@nephro/redux/stores/app/actions';

// Helpers
import {
  getStorageItem,
  removeAllStorageItem
} from '@nephro/commons/helpers/storage-helper';

// Constants
import {
  LOG_IN,
  SIGN_OUT,
  VERIFY_AUTH,
  RESET_PASSWORD,
  RECOVER_PASSWORD,
  GET_CLAIMS,
  GET_USER_LIST,
  SEND_CODE,
  VERIFY_CODE,
  GET_TOKEN,
  AUTO_ENROLL_FACTOR,
  RESET_PASSWORD_REGISTER,
  ACTIVATE_PATIENT_BY_REGISTER
} from './constants';

/**
 * Authenticate user
 * @param { object } action - Action with payload data
 */
export function* logIn(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));

  const {
    payload: { data },
    callback
  } = action;
  const { userName, credentials, identityProvider, isEmail, documentType } =
    data;

  try {
    const response = yield call(
      authenticationService.login,
      userName,
      credentials,
      identityProvider,
      isEmail,
      documentType
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        false,
        { ...err, data: { ...data } },
        callback
      )
    );
  }
  yield put(setLoading(false));
}

/**
 * Reset user password
 * @param { object } action - Action with payload data
 */
export function* resetPassword(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));

  const {
    payload: { data },
    callback
  } = action;
  const { email, userNewPassword } = data;

  try {
    const response = yield call(
      authenticationService.resetPassword,
      email,
      userNewPassword
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
    yield put(setLoading(false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        false,
        { ...err, data: { ...data } },
        callback
      )
    );
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

export function* passwordResetRegister(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));

  const {
    payload: { data },
    callback
  } = action;
  const { email, userNewPassword, identificationDocument, documentType } = data;

  try {
    const response = yield call(
      authenticationService.passwordResetRegister,
      identificationDocument,
      documentType,
      email,
      userNewPassword
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
    yield put(setLoading(false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        false,
        { ...err, data: { ...data } },
        callback
      )
    );
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

export function* activatePatientByRegister(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));

  const {
    payload: { data },
    callback
  } = action;
  const { email, identificationDocument, documentType } = data;

  try {
    const response = yield call(
      authenticationService.activatePatientByRegister,
      identificationDocument,
      documentType,
      email
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
    yield put(setLoading(false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        false,
        { ...err, data: { ...data } },
        callback
      )
    );
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Recover user password
 * @param { object } action - Action with payload data
 */
export function* recoverPassword(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));

  const {
    payload: { data },
    callback
  } = action;
  const { email } = data;

  try {
    const response = yield call(authenticationService.recoverPassword, email);
    yield put(setResponse('success', false, false, { ...response }, callback));
    yield put(setLoading(false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        false,
        { ...err, data: { ...data } },
        callback
      )
    );
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Start process to validate session
 * @param { object } action - Action with payload data
 */
export function* verifyAuth(action) {
  yield put(setLoading(true));
  const { callback } = action;

  try {
    const userInfo = getStorageItem('user');
    yield put(setUser(userInfo));
    yield put(setResponse('success', false, false, userInfo, callback));
  } catch (err) {
    yield put(setResponse('error', false, false, err.message, callback));
  }

  yield put(setLoading(false));
}

/**
 * Call process to sign out user
 * @param { object } action - Action with payload data
 */
export function* signOut(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));

  const {
    callback,
    payload: { data }
  } = action;
  const { identityProvider, userName } = data;

  try {
    const response = yield call(
      authenticationService.signOut,
      identityProvider,
      userName
    );
    removeAllStorageItem();
    yield put(setUser(false));
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(setResponse('error', false, false, err.message, callback));
  }

  yield put(setLoading(false));
}

/**
 * Recover user password
 * @param { object } action - Action with payload data
 */
export function* getClaims(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));

  const {
    payload: { data },
    callback
  } = action;
  const { token } = data;

  try {
    const response = yield call(authenticationService.getClaims, token);
    yield put(setResponse('success', false, false, { ...response }, callback));
    yield put(setLoading(false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        false,
        { ...err, data: { ...data } },
        callback
      )
    );
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Get user list
 * @param { object } action - Action with payload data
 */
export function* getUserList(action) {
  yield put(setResponse(false, false, false));

  const {
    payload: { data },
    callback
  } = action;
  const { identityProvider, filter } = data;

  try {
    const response = yield call(
      authenticationService.getUserList,
      identityProvider,
      filter
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        false,
        { ...err, data: { ...data } },
        callback
      )
    );
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Send code OTP.
 * @param { object } action - Action with payload data
 */
export function* sendCode(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));

  const {
    payload: { data },
    callback
  } = action;
  const { stateToken, factorId, identityProvider } = data;

  try {
    const response = yield call(
      authenticationService.sendCode,
      stateToken,
      factorId,
      identityProvider
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        false,
        { ...err, data: { ...data } },
        callback
      )
    );
  }
  yield put(setLoading(false));
}

/**
 * Verify code OTP.
 * @param { object } action - Action with payload data
 */
export function* verifyCode(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));

  const {
    payload: { data },
    callback
  } = action;
  const { username, passcode, stateToken, factorId, identityProvider } = data;

  try {
    const response = yield call(
      authenticationService.verifyCode,
      username,
      passcode,
      stateToken,
      factorId,
      identityProvider
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        false,
        { ...err, data: { ...data } },
        callback
      )
    );
  }
  yield put(setLoading(false));
}

/**
 * Get Token
 * @param { object } action - Action with payload data
 */
export function* getToken(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));

  const {
    payload: { data },
    callback
  } = action;
  const { sessionToken, identityProvider } = data;

  try {
    const response = yield call(
      authenticationService.getToken,
      sessionToken,
      identityProvider
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        false,
        { ...err, data: { ...data } },
        callback
      )
    );
  }
  yield put(setLoading(false));
}

/**
 * Get Token
 * @param { object } action - Action with payload data
 */
export function* autoEnrollFactor(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));

  const {
    payload: { data },
    callback
  } = action;
  const { username, accessToken, identityProvider } = data;

  try {
    const response = yield call(
      authenticationService.autoEnrollFactor,
      username,
      accessToken,
      identityProvider
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        false,
        { ...err, data: { ...data } },
        callback
      )
    );
  }
  yield put(setLoading(false));
}

/**
 * Declare all component sagas
 */
export function* watchAuthSaga() {
  yield takeLatest(LOG_IN, logIn);
  yield takeLatest(RESET_PASSWORD, resetPassword);
  yield takeLatest(RESET_PASSWORD_REGISTER, passwordResetRegister);
  yield takeLatest(ACTIVATE_PATIENT_BY_REGISTER, activatePatientByRegister);
  yield takeLatest(RECOVER_PASSWORD, recoverPassword);
  yield takeLatest(VERIFY_AUTH, verifyAuth);
  yield takeLatest(SIGN_OUT, signOut);
  yield takeLatest(GET_CLAIMS, getClaims);
  yield takeLatest(GET_USER_LIST, getUserList);
  yield takeLatest(SEND_CODE, sendCode);
  yield takeLatest(VERIFY_CODE, verifyCode);
  yield takeLatest(GET_TOKEN, getToken);
  yield takeLatest(AUTO_ENROLL_FACTOR, autoEnrollFactor);
}
